import { useState, useEffect } from 'react';
import { Settings, Globe, DollarSign, Heart, Info, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, authFetch } from './helpers';

const SettingsTab = () => {
  const [form, setForm] = useState({ site_name: '', custom_domain: '', paypal_link: '', cashapp_link: '', zeffy_link: '', facebook_url: '', instagram_url: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const res = await authFetch(`${API}/settings`);
      if (res.ok) {
        const s = await res.json();
        setForm({
          site_name: s.site_name || '', custom_domain: s.custom_domain || '',
          paypal_link: s.paypal_link || '', cashapp_link: s.cashapp_link || '',
          zeffy_link: s.zeffy_link || '', facebook_url: s.facebook_url || '',
          instagram_url: s.instagram_url || ''
        });
      }
    };
    fetchSettings();
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    await authFetch(`${API}/settings`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div data-testid="settings-tab">
      <h2 className="text-xl font-semibold text-brand-dark mb-6">Site Settings</h2>
      <form onSubmit={saveSettings} className="space-y-8">
        {/* General */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4"><Globe className="w-5 h-5 text-brand-gold" /><h3 className="text-lg font-medium text-brand-dark">General</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-brand-dark font-medium">Site Name</Label><Input value={form.site_name} onChange={(e) => setForm({...form, site_name: e.target.value})} className="mt-1" data-testid="settings-site-name" /></div>
            <div><Label className="text-brand-dark font-medium">Custom Domain</Label><Input value={form.custom_domain} onChange={(e) => setForm({...form, custom_domain: e.target.value})} className="mt-1" placeholder="elevatingmn.com" data-testid="settings-domain" /></div>
          </div>
        </div>

        {/* Donations */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4"><DollarSign className="w-5 h-5 text-brand-gold" /><h3 className="text-lg font-medium text-brand-dark">Donation Setup</h3></div>
          <div className="bg-green-50 border border-green-200 p-4 mb-4">
            <div className="flex gap-3">
              <Heart className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">Using Zeffy for donations</p>
                <p>Zeffy is 100% free for nonprofits. Paste your Zeffy campaign URL below and it will appear as a floating "Donate" button site-wide.</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div><Label className="text-brand-dark font-medium">Zeffy Campaign URL</Label><Input value={form.zeffy_link} onChange={(e) => setForm({...form, zeffy_link: e.target.value})} className="mt-1" placeholder="https://www.zeffy.com/en-US/donation-form/..." data-testid="settings-zeffy" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-brand-dark font-medium">PayPal Link (optional)</Label><Input value={form.paypal_link} onChange={(e) => setForm({...form, paypal_link: e.target.value})} className="mt-1" placeholder="https://paypal.me/..." /></div>
              <div><Label className="text-brand-dark font-medium">CashApp Link (optional)</Label><Input value={form.cashapp_link} onChange={(e) => setForm({...form, cashapp_link: e.target.value})} className="mt-1" placeholder="https://cash.app/..." /></div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4"><ExternalLink className="w-5 h-5 text-brand-gold" /><h3 className="text-lg font-medium text-brand-dark">Social Media</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-brand-dark font-medium">Facebook URL</Label><Input value={form.facebook_url} onChange={(e) => setForm({...form, facebook_url: e.target.value})} className="mt-1" placeholder="https://facebook.com/ElevateMN" data-testid="settings-facebook" /></div>
            <div><Label className="text-brand-dark font-medium">Instagram URL</Label><Input value={form.instagram_url} onChange={(e) => setForm({...form, instagram_url: e.target.value})} className="mt-1" placeholder="https://instagram.com/ElevateMN" data-testid="settings-instagram" /></div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button type="submit" className="bg-brand-gold px-8 py-3 text-sm font-semibold text-brand-dark hover:opacity-90" data-testid="save-settings-btn">Save Settings</button>
          {saved && <span className="text-green-600 text-sm" data-testid="settings-saved-msg">Settings saved!</span>}
        </div>
      </form>
    </div>
  );
};

export default SettingsTab;
